import {useEffect, useMemo, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import {ColumnDef} from '@tanstack/react-table';
import {Button as BootstrapButton, Card, Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import {saveAs} from 'file-saver';
import jsPDF from 'jspdf';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import FilterSelect, {FilterOption} from '../../../../../app/common/components/select/FilterSelect';
import Loading from '../../../../../app/common/components/Loading';
import {CONSO_TRIS, CONSO_TYPES, AcdCall, ConsoCall, ConsoCaller, ConsoDestination} from './mocks';
import {genererPeriodesDisponibles, NOMBRE_MOIS_PERIODE} from './periodesFacturation';
import {
  useGetCallsPage,
  useGetTrunkNumeros,
  useGetTrunkSipConsommation,
} from '../../../Hooks/TelephonieManager';
import {CallDirection, symboleDevise} from '../../../Api/telephonie';

const TOUTES_LES_LIGNES = 'Toutes les lignes';

/**
 * Les tickets d'appels VTX (`NOCLID`) et la liste des numéros de la prestation
 * n'utilisent pas le même format : `"0033 4 22 22 77 60"` côté appels contre
 * `"+33422227760"` côté numéros. On compare donc sur les seuls chiffres, en
 * traitant le préfixe international `00` comme équivalent au `+`.
 */
const normaliserNumero = (numero: string): string => {
  const chiffres = numero.replace(/\D/g, '');
  return chiffres.startsWith('00') ? chiffres.slice(2) : chiffres;
};

/** Taille de page du tableau « Appels sortants », chargé depuis l'API page par page. */
const APPELS_PAGE_SIZE = 10;

const TYPE_LABEL_KEYS: Record<string, string> = {
  'Appels sortants': 'TELEPHONIE.CONSOMMATION.TYPE.SORTANTS',
  'Appels entrants': 'TELEPHONIE.CONSOMMATION.TYPE.ENTRANTS',
};

const TRI_LABEL_KEYS: Record<string, string> = {
  'Ordre chronologique': 'TELEPHONIE.CONSOMMATION.TRI.CHRONOLOGIQUE',
  'Ordre chronologique inversé': 'TELEPHONIE.CONSOMMATION.TRI.CHRONOLOGIQUE_INVERSE',
  'Par numéro appelant': 'TELEPHONIE.CONSOMMATION.TRI.PAR_NUMERO',
};

const KPI_LABEL_KEYS: Record<string, string> = {
  'Coût total du mois': 'TELEPHONIE.CONSOMMATION.KPI.COUT_TOTAL_MOIS',
  'Durée totale': 'TELEPHONIE.CONSOMMATION.COL_DUREE_TOTALE',
  'Appels sortants': 'TELEPHONIE.CONSOMMATION.TYPE.SORTANTS',
  'Appels entrants': 'TELEPHONIE.CONSOMMATION.TYPE.ENTRANTS',
};

const traduireKpiLabel = (label: string, intl: IntlShape): string =>
  KPI_LABEL_KEYS[label] ? intl.formatMessage({id: KPI_LABEL_KEYS[label]}) : label;

const traduireKpiSub = (sub: string, intl: IntlShape): string => {
  if (sub === 'HT') return intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.KPI.HT'});
  if (sub === 'reçus ce mois') {
    return intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.KPI.RECUS_CE_MOIS'});
  }
  const matchVoix = sub.match(/^(.+) de voix$/);
  if (matchVoix) {
    return intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.KPI.DE_VOIX'}, {duree: matchVoix[1]});
  }
  const matchDest = sub.match(/^vers (\d+) destinations?$/);
  if (matchDest) {
    return intl.formatMessage(
      {id: 'TELEPHONIE.CONSOMMATION.KPI.VERS_DESTINATIONS'},
      {count: Number(matchDest[1])}
    );
  }
  return sub;
};

const toTranslatedOptions = (
  values: string[],
  labelKeys: Record<string, string>,
  intl: IntlShape
): FilterOption<string>[] =>
  values.map((value) => ({
    value,
    label: labelKeys[value] ? intl.formatMessage({id: labelKeys[value]}) : value,
  }));

const formatDuration = (totalSeconds: number): string => {
  const heures = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secondes = totalSeconds % 60;
  const parts = heures > 0 ? [`${String(heures).padStart(2, '0')}h`] : [];
  parts.push(`${String(minutes).padStart(2, '0')}mn`, `${String(secondes).padStart(2, '0')}s`);
  return parts.join(' ');
};

const formatCout = (value: number, devise: string): string =>
  `${value.toFixed(2).replace('.', ',')} ${symboleDevise(devise)}`;

/**
 * `callDate` est renvoyé par api-telephonie suffixé `+00:00`, mais correspond en
 * réalité déjà à l'heure locale Paris/Zurich (confirmé empiriquement : un appel à
 * « 15:59:19+00:00 » a réellement eu lieu à 15h59 heure locale, pas 17h59). On
 * garde donc le décalage tel quel (`setZone: true`) au lieu de laisser Luxon le
 * convertir vers le fuseau du navigateur, ce qui ajouterait 1h ou 2h en trop.
 */
const parseCallDate = (iso: string): DateTime => DateTime.fromISO(iso, {setZone: true});

const toTimestamp = (call: ConsoCall): number => parseCallDate(call.date).toMillis();

/**
 * L'appel API est déjà scopé au mois demandé ; ce filtre ne sert donc qu'à ignorer
 * un éventuel appel mal daté renvoyé par l'API.
 */
const matchesPeriode = (
  call: ConsoCall,
  periode: string,
  periodeMoisApi: Record<string, string>
): boolean => parseCallDate(call.date).toFormat('yyyy-MM') === periodeMoisApi[periode];

const sortCalls = (calls: ConsoCall[], tri: string): ConsoCall[] => {
  const sorted = [...calls];
  switch (tri) {
    case 'Par numéro appelant':
      return sorted.sort((a, b) => a.numAppelant.localeCompare(b.numAppelant));
    case 'Ordre chronologique inversé':
      return sorted.sort((a, b) => toTimestamp(b) - toTimestamp(a));
    case 'Ordre chronologique':
    default:
      return sorted.sort((a, b) => toTimestamp(a) - toTimestamp(b));
  }
};

const sortCallers = (callers: ConsoCaller[], tri: string): ConsoCaller[] => {
  const sorted = [...callers];
  if (tri === 'Par numéro appelant') {
    return sorted.sort((a, b) => a.numero.localeCompare(b.numero));
  }
  return sorted.sort((a, b) => b.nbAppels - a.nbAppels);
};

const sortDestinations = (destinations: ConsoDestination[]): ConsoDestination[] =>
  [...destinations].sort((a, b) => b.nbAppels - a.nbAppels);

const aggregateByCaller = (calls: ConsoCall[], devise: string): ConsoCaller[] => {
  const parCaller = new Map<string, ConsoCall[]>();
  calls.forEach((call) => {
    parCaller.set(call.numAppelant, [...(parCaller.get(call.numAppelant) ?? []), call]);
  });
  return Array.from(parCaller.entries()).map(([numero, callsDuNumero]) => ({
    numero,
    nbAppels: callsDuNumero.length,
    duree: formatDuration(callsDuNumero.reduce((total, c) => total + c.duree, 0)),
    destinations: new Set(callsDuNumero.map((c) => c.destination)).size,
    cout: formatCout(
      callsDuNumero.reduce((total, c) => total + c.cout, 0),
      devise
    ),
  }));
};

const aggregateByDestination = (calls: ConsoCall[], devise: string): ConsoDestination[] => {
  const parDestination = new Map<string, ConsoCall[]>();
  calls.forEach((call) => {
    parDestination.set(call.destination, [...(parDestination.get(call.destination) ?? []), call]);
  });
  return Array.from(parDestination.entries()).map(([destination, callsDeLaDestination]) => ({
    destination,
    nbAppels: callsDeLaDestination.length,
    duree: formatDuration(callsDeLaDestination.reduce((total, c) => total + c.duree, 0)),
    destinations: new Set(callsDeLaDestination.map((c) => c.numAppelant)).size,
    cout: formatCout(
      callsDeLaDestination.reduce((total, c) => total + c.cout, 0),
      devise
    ),
  }));
};

/**
 * Onglet « Suivi de consommation » d'une prestation Trunk SIP.
 *
 * Les appels (`calls`) viennent de l'API (appels facturés du mois sélectionné) ;
 * le récapitulatif par n° appelant, par destination et les totaux sont recalculés
 * ci-dessous à partir de ces appels, pour que Type/Ligne/Tri aient un effet réel et
 * cohérent sur tous les tableaux de la page. Les KPI, la progression du forfait et
 * les appels ACD restent des données de maquette : l'API ne porte pas encore
 * d'agrégats d'abonnement ni de données de centre d'appels.
 *
 * Le tableau « Récapitulatif des appels » est une exception : il est chargé
 * page par page depuis l'API (`useGetCallsPage`), pas depuis `calls` — c'est
 * le seul tableau de l'écran dont la pagination déclenche un appel réseau
 * plutôt qu'un slicing local. Filtre Type (sortants/entrants — « Tous les
 * appels » retombe sur les sortants) et Ligne pilotent la requête (direction
 * et paramètre `caller`) et le titre du tableau ; Tri « Ordre chronologique »
 * / « inversé » pilote aussi la requête (paramètre `order`, relayé jusqu'à
 * api-telephonie) — seul « Par numéro appelant » n'a pas d'équivalent côté
 * API et ne s'applique donc pas à ce tableau. Les exports (CSV/Excel/PDF)
 * restent basés sur `sortedCalls`, donc sur le mois entier, indépendamment de
 * la page affichée — sa section « Récapitulatif des appels sortants » reste
 * elle aussi toujours limitée aux sortants, indépendamment du filtre Type.
 */
interface SuiviConsommationProps {
  serviceId: number;
  isCentrex?: boolean;
  /**
   * Numéro à présélectionner dans le filtre Ligne, ex. depuis l'action « Consommation »
   * de l'onglet Gérer vos numéros. Le `cle` doit changer à chaque demande (même numéro
   * inclus) pour que la présélection reprenne la main sur un filtre Ligne déjà modifié.
   */
  numeroAPreselectionner?: {numero: string; cle: number} | null;
}

const SuiviConsommation = ({
  serviceId,
  isCentrex = false,
  numeroAPreselectionner,
}: SuiviConsommationProps): JSX.Element | null => {
  const intl = useIntl();

  // Fenêtre glissante calculée au moment de l'affichage (pas une date en dur) :
  // mois courant en tête, sélectionné par défaut.
  const periodesOptions = useMemo(
    () => genererPeriodesDisponibles(intl, NOMBRE_MOIS_PERIODE),
    [intl]
  );

  const periodesDisponibles = useMemo(
    () => periodesOptions.map((option) => option.label),
    [periodesOptions]
  );

  const periodeMoisApi = useMemo(
    () => Object.fromEntries(periodesOptions.map((option) => [option.label, option.moisApi])),
    [periodesOptions]
  );

  const [periode, setPeriode] = useState<string>(periodesOptions[0].label);
  const [type, setType] = useState<string>(CONSO_TYPES[0]);
  const [tri, setTri] = useState<string>(CONSO_TRIS[0]);
  const [ligne, setLigne] = useState<string>(TOUTES_LES_LIGNES);

  useEffect(() => {
    if (numeroAPreselectionner) {
      setLigne(numeroAPreselectionner.numero);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeroAPreselectionner?.cle]);

  const periodeMoisApiCourant = periodeMoisApi[periode];
  const {data, isLoading, isError} = useGetTrunkSipConsommation(serviceId, periodeMoisApiCourant);
  const {data: trunkNumeros} = useGetTrunkNumeros(serviceId);
  const devise = data?.devise ?? 'EUR';

  // L'API attend le format brut exact de `NOCLID` (ex. "0033 4 22 22 77 60"),
  // différent du format E.164 du menu Ligne (ex. "+33422227760") : on retrouve
  // ce format brut sur un appel déjà chargé plutôt que de le reconstruire.
  // À défaut (aucun appel ce mois-ci pour ce numéro), la valeur E.164 ne
  // correspondra à aucun `NOCLID` — ce qui est le résultat attendu.
  const callerFiltre =
    ligne !== TOUTES_LES_LIGNES
      ? (data?.calls ?? []).find(
          (call) => normaliserNumero(call.numAppelant) === normaliserNumero(ligne)
        )?.numAppelant ?? ligne
      : undefined;
  const directionAppels: CallDirection = type === 'Appels entrants' ? 'inbound' : 'outbound';
  // Seul « Ordre chronologique inversé » a un équivalent côté API ; « Par
  // numéro appelant » retombe sur l'ordre par défaut (croissant) pour ce
  // tableau, faute de tri par numéro côté api-telephonie.
  const ordreAppels: 'asc' | 'desc' = tri === 'Ordre chronologique inversé' ? 'desc' : 'asc';
  const [appelsPageIndex, setAppelsPageIndex] = useState(0);

  // Le filtre Type/Ligne/la période/l'ordre change le jeu de résultats : on
  // revient à la première page plutôt que de rester sur une page qui n'existe
  // plus ou dont le contenu a changé sous les pieds de l'utilisateur.
  useEffect(() => {
    setAppelsPageIndex(0);
  }, [serviceId, periodeMoisApiCourant, directionAppels, callerFiltre, ordreAppels]);

  const {data: appelsPage, isLoading: isLoadingAppelsPage} = useGetCallsPage(
    serviceId,
    periodeMoisApiCourant,
    directionAppels,
    appelsPageIndex + 1,
    APPELS_PAGE_SIZE,
    callerFiltre,
    ordreAppels
  );

  const titreRecapAppels = useMemo(() => {
    const idsByDirection =
      directionAppels === 'inbound'
        ? {
            tous: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELS_ENTRANTS',
            numero: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELS_ENTRANTS_NUMERO',
          }
        : {
            tous: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELS_SORTANTS',
            numero: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELS_SORTANTS_NUMERO',
          };

    return ligne === TOUTES_LES_LIGNES
      ? intl.formatMessage({id: idsByDirection.tous})
      : intl.formatMessage({id: idsByDirection.numero}, {numero: ligne});
  }, [directionAppels, ligne, intl]);

  const ligneOptions = useMemo(() => {
    // Un numéro réservé n'a pas de trafic à suivre : on ne le propose pas.
    const numeros = (trunkNumeros?.numeros ?? [])
      .filter((numero) => !numero.reserve)
      .map((numero) => numero.numero)
      .sort();
    return [TOUTES_LES_LIGNES, ...numeros];
  }, [trunkNumeros]);

  const ligneSelectOptions = useMemo<FilterOption<string>[]>(
    () =>
      ligneOptions.map((value) =>
        value === TOUTES_LES_LIGNES
          ? {value, label: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TOUTES_LES_LIGNES'})}
          : {value, label: value}
      ),
    [ligneOptions, intl]
  );

  const periodeSelectOptions = useMemo<FilterOption<string>[]>(
    () => periodesDisponibles.map((label) => ({value: label, label})),
    [periodesDisponibles]
  );
  const typeSelectOptions = useMemo<FilterOption<string>[]>(
    () => toTranslatedOptions(CONSO_TYPES, TYPE_LABEL_KEYS, intl),
    [intl]
  );
  const triSelectOptions = useMemo<FilterOption<string>[]>(
    () => toTranslatedOptions(CONSO_TRIS, TRI_LABEL_KEYS, intl),
    [intl]
  );

  const filteredCalls = useMemo(() => {
    const calls = data?.calls ?? [];
    return calls.filter((call) => {
      if (type === 'Appels sortants' && call.type !== 'Sortant') return false;
      if (type === 'Appels entrants' && call.type !== 'Entrant') return false;
      if (
        ligne !== TOUTES_LES_LIGNES &&
        normaliserNumero(call.numAppelant) !== normaliserNumero(ligne)
      )
        return false;
      return matchesPeriode(call, periode, periodeMoisApi);
    });
  }, [data, type, ligne, periode, periodeMoisApi]);

  const sortedCalls = useMemo(
    () =>
      sortCalls(
        filteredCalls.filter((call) => call.type === 'Sortant'),
        tri
      ),
    [filteredCalls, tri]
  );
  const callers = useMemo(
    () => sortCallers(aggregateByCaller(filteredCalls, devise), tri),
    [filteredCalls, devise, tri]
  );
  const destinations = useMemo(
    () => sortDestinations(aggregateByDestination(filteredCalls, devise)),
    [filteredCalls, devise]
  );
  const total = useMemo(
    () => ({
      nbAppels: filteredCalls.length,
      duree: formatDuration(filteredCalls.reduce((sum, c) => sum + c.duree, 0)),
      destinations: new Set(filteredCalls.map((c) => c.destination)).size,
      cout: formatCout(
        filteredCalls.reduce((sum, c) => sum + c.cout, 0),
        devise
      ),
    }),
    [filteredCalls, devise]
  );

  /**
   * Répartition sortants/entrants de la période (Ligne comprise, mais pas le
   * filtre Type — sinon sélectionner « Appels sortants » ferait afficher 0 sur
   * la carte « Appels entrants »).
   */
  const callsPeriodeEtLigne = useMemo(() => {
    const calls = data?.calls ?? [];
    return calls.filter((call) => {
      if (
        ligne !== TOUTES_LES_LIGNES &&
        normaliserNumero(call.numAppelant) !== normaliserNumero(ligne)
      )
        return false;
      return matchesPeriode(call, periode, periodeMoisApi);
    });
  }, [data, ligne, periode, periodeMoisApi]);

  const repartitionParType = useMemo(() => {
    const sortants = callsPeriodeEtLigne.filter((call) => call.type === 'Sortant');
    const entrants = callsPeriodeEtLigne.filter((call) => call.type === 'Entrant');
    return {
      nbSortants: sortants.length,
      nbEntrants: entrants.length,
      destinationsSortants: new Set(sortants.map((call) => call.destination)).size,
    };
  }, [callsPeriodeEtLigne]);

  const callerColumns = useMemo<ColumnDef<ConsoCaller>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NUMERO_APPELANT'}),
        accessorKey: 'numero',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NB_APPELS'}),
        accessorKey: 'nbAppels',
        size: 110,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_TOTALE'}),
        accessorKey: 'duree',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATIONS_DISTINCTES'}),
        accessorKey: 'destinations',
        size: 190,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_TOTAL_HT'}),
        accessorKey: 'cout',
        size: 130,
      },
    ],
    [intl]
  );

  const destinationColumns = useMemo<ColumnDef<ConsoDestination>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATION'}),
        accessorKey: 'destination',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NB_APPELS'}),
        accessorKey: 'nbAppels',
        size: 110,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_TOTALE'}),
        accessorKey: 'duree',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATIONS_DISTINCTES'}),
        accessorKey: 'destinations',
        size: 190,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_TOTAL_HT'}),
        accessorKey: 'cout',
        size: 110,
      },
    ],
    [intl]
  );

  const callColumns = useMemo<ColumnDef<ConsoCall>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'COMMON.DATE'}),
        accessorKey: 'date',
        size: 110,
        cell: (info) => parseCallDate(info.row.original.date).toFormat('dd/MM/yyyy'),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_HEURE'}),
        id: 'heure',
        size: 90,
        enableSorting: false,
        cell: (info) => parseCallDate(info.row.original.date).toFormat('HH:mm'),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_APPELE'}),
        accessorKey: 'appele',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NOM_APPELANT'}),
        accessorKey: 'nomAppelant',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NUM_APPELANT'}),
        accessorKey: 'numAppelant',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATION'}),
        accessorKey: 'destination',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE'}),
        accessorKey: 'duree',
        size: 120,
        cell: (info) => formatDuration(info.row.original.duree),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_HT'}),
        accessorKey: 'cout',
        size: 110,
        cell: (info) => formatCout(info.row.original.cout, devise),
      },
    ],
    [intl, devise]
  );

  const acdColumns = useMemo<ColumnDef<AcdCall>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DATE_HEURE'}),
        accessorKey: 'dateHeure',
        size: 180,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_APPELANT'}),
        accessorKey: 'appelant',
        size: 160,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NOM_UTILISATEUR'}),
        accessorKey: 'utilisateur',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NUM_APPELE'}),
        accessorKey: 'numAppele',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_FACTUREE'}),
        accessorKey: 'duree',
        size: 150,
        cell: (info) => formatDuration(info.row.original.duree),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_MONTANT_HT'}),
        accessorKey: 'montant',
        size: 130,
        cell: (info) => formatCout(info.row.original.montant, devise),
      },
    ],
    [intl, devise]
  );

  const filtersToolbar = (
    <>
      <FilterSelect options={periodeSelectOptions} value={periode} onChange={setPeriode} />
      <FilterSelect options={typeSelectOptions} value={type} onChange={setType} />
      <FilterSelect options={triSelectOptions} value={tri} onChange={setTri} />
      <FilterSelect options={ligneSelectOptions} value={ligne} onChange={setLigne} />
    </>
  );

  if (isError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const {kpis, forfait, acdCalls} = data;

  // Coût total, durée totale et compteurs sortants/entrants viennent des appels
  // réels ; seul le forfait (abonnement) et les appels ACD restent des données de
  // maquette, l'API n'ayant pas encore ces agrégats.
  const kpisAffiches = kpis.map((kpi) => {
    switch (kpi.label) {
      case 'Coût total du mois':
        return {...kpi, value: total.cout};
      case 'Durée totale':
        return {...kpi, value: total.duree, sub: ''};
      case 'Appels sortants':
        return {
          ...kpi,
          value: String(repartitionParType.nbSortants),
          sub: `vers ${repartitionParType.destinationsSortants} destination${
            repartitionParType.destinationsSortants === 1 ? '' : 's'
          }`,
        };
      case 'Appels entrants':
        return {...kpi, value: String(repartitionParType.nbEntrants)};
      default:
        return kpi;
    }
  });

  const buildReportSections = (): {title: string; rows: string[][]}[] => [
    {
      title: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_KPI_PERIODE'}),
      rows: [
        [
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_INDICATEUR'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_VALEUR'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DETAIL'}),
        ],
        ...kpisAffiches.map((kpi) => [
          traduireKpiLabel(kpi.label, intl),
          kpi.value,
          traduireKpiSub(kpi.sub, intl),
        ]),
      ],
    },
    {
      title: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.FORFAIT_TITLE'}),
      rows: [
        [
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_CONSOMMEE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_MONTANT'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.PCT_CONSOMME'}),
        ],
        [
          forfait.dureeConsommee,
          forfait.montant,
          `${forfait.pourcentage.toLocaleString('fr-FR')} %`,
        ],
      ],
    },
    {
      title: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELANT'}),
      rows: [
        [
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NUMERO_APPELANT'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NB_APPELS'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_TOTALE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATIONS_DISTINCTES'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_TOTAL_HT'}),
        ],
        ...callers.map((c) => [
          c.numero,
          String(c.nbAppels),
          c.duree,
          String(c.destinations),
          c.cout,
        ]),
        [
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_TOTAL'}),
          String(total.nbAppels),
          total.duree,
          String(total.destinations),
          total.cout,
        ],
      ],
    },
    {
      title: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_DESTINATIONS'}),
      rows: [
        [
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATION'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NB_APPELS'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE_TOTALE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATIONS_DISTINCTES'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_TOTAL_HT'}),
        ],
        ...destinations.map((d) => [
          d.destination,
          String(d.nbAppels),
          d.duree,
          String(d.destinations),
          d.cout,
        ]),
      ],
    },
    {
      title: intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELS_SORTANTS'}),
      rows: [
        [
          intl.formatMessage({id: 'COMMON.DATE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_HEURE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_APPELE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NOM_APPELANT'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_NUM_APPELANT'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DESTINATION'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_DUREE'}),
          intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.COL_COUT_HT'}),
        ],
        ...sortedCalls.map((c) => [
          parseCallDate(c.date).toFormat('dd/MM/yyyy'),
          parseCallDate(c.date).toFormat('HH:mm'),
          c.appele,
          c.nomAppelant,
          c.numAppelant,
          c.destination,
          formatDuration(c.duree),
          formatCout(c.cout, devise),
        ]),
      ],
    },
  ];

  const handleExportCsv = (): void => {
    const csv = buildReportSections()
      .map((section) => [section.title, ...section.rows.map((row) => row.join(';'))].join('\n'))
      .join('\n\n');
    const blob = new Blob([`﻿${csv}`], {type: 'text/csv;charset=utf-8'});
    saveAs(blob, `suivi-consommation-${periode}.csv`);
  };

  const handleExportExcel = (): void => {
    const sectionsHtml = buildReportSections()
      .map(
        (section) => `
          <h3>${section.title}</h3>
          <table border="1">
            ${section.rows
              .map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`)
              .join('')}
          </table>`
      )
      .join('<br/>');
    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8" /></head>
        <body>${sectionsHtml}</body>
      </html>`;
    const blob = new Blob([html], {type: 'application/vnd.ms-excel;charset=utf-8'});
    saveAs(blob, `suivi-consommation-${periode}.xls`);
  };

  const handleExportPdf = (): void => {
    // Généré à partir de `buildReportSections()` (données complètes) plutôt que
    // via `window.print()` : celui-ci n'aurait imprimé que la page actuellement
    // visible des tableaux paginés à l'écran, avec les menus/filtres autour.
    const doc = new jsPDF({orientation: 'landscape', unit: 'mm', format: 'a4'});
    const marginX = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - marginX * 2;
    const lineHeight = 4;
    let y = 15;

    const ensureSpace = (needed: number): void => {
      if (y + needed > pageHeight - 10) {
        doc.addPage();
        y = 15;
      }
    };

    buildReportSections().forEach(({title, rows: [header, ...rows]}, sectionIndex) => {
      if (sectionIndex > 0) y += 6;
      ensureSpace(14);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginX, y);
      y += 7;

      const colWidth = usableWidth / header.length;
      doc.setFontSize(8);

      const printRow = (row: string[], bold: boolean): void => {
        const cellLines = row.map((cell) => doc.splitTextToSize(cell, colWidth - 2));
        const rowHeight = Math.max(...cellLines.map((lines) => lines.length)) * lineHeight + 2;
        ensureSpace(rowHeight);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        let x = marginX;
        cellLines.forEach((lines) => {
          doc.text(lines, x, y);
          x += colWidth;
        });
        y += rowHeight;
      };

      printRow(header, true);
      rows.forEach((row) => printRow(row, false));
    });

    doc.save(`suivi-consommation-${periode}.pdf`);
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-end gap-3 mb-5">
        <BootstrapButton variant="outline-secondary" size="sm" onClick={handleExportCsv}>
          <i className="bi bi-filetype-csv me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.EXPORT_CSV'})}
        </BootstrapButton>
        <BootstrapButton variant="outline-secondary" size="sm" onClick={handleExportExcel}>
          <i className="bi bi-file-earmark-spreadsheet me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.EXPORT_EXCEL'})}
        </BootstrapButton>
        <BootstrapButton variant="outline-secondary" size="sm" onClick={handleExportPdf}>
          <i className="bi bi-filetype-pdf me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.EXPORT_PDF'})}
        </BootstrapButton>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-5">{filtersToolbar}</div>

      {/* Indicateurs de la période */}
      <Row className="g-4 mb-5">
        {kpisAffiches.map((kpi) => (
          <Col key={kpi.label} xs={12} sm={6} xl={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted fs-6 mb-2">{traduireKpiLabel(kpi.label, intl)}</div>
                <div className="fs-2 fw-bold">{kpi.value}</div>
                <div className="text-muted fs-7 mt-1">{traduireKpiSub(kpi.sub, intl)}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/*
        Progression du forfait masquée temporairement (données pas encore fiabilisées
        côté API) : sera réaffichée lors d'un développement dédié.
        <Card className="mb-5">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
              <span className="text-muted fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.FORFAIT_TITLE'})}
              </span>
              <div className="d-flex align-items-center gap-4">
                <span className="text-muted fs-6">
                  <i className="bi bi-clock me-2" />
                  {forfait.dureeConsommee}
                </span>
                <span className="fs-6 fw-semibold">{forfait.montant}</span>
              </div>
            </div>
            <ProgressBar now={forfait.pourcentage} variant="primary" style={{height: '10px'}} />
            <div className="text-muted fs-7 mt-2">
              {forfait.pourcentage.toLocaleString('fr-FR')}{' '}
              {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.PCT_CONSOMME'})}
            </div>
          </Card.Body>
        </Card>
      */}

      <div className="mb-5">
        <TableMetronic<ConsoCaller>
          title={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_APPELANT'})}
          data={callers}
          columns={callerColumns}
          stackedHeader
          smallSearchBar
          elementName={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_NUMERO'})}
          elementNamePlural={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_NUMEROS'})}
        />
      </div>

      <div className="mb-5">
        <TableMetronic<ConsoDestination>
          title={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_RECAP_DESTINATIONS'})}
          data={destinations}
          columns={destinationColumns}
          searchBar={false}
          subtitle={<span className="text-muted fs-6">{`${periode} · ${type}`}</span>}
        />
      </div>

      <div className="mb-5">
        <TableMetronic<ConsoCall>
          title={titreRecapAppels}
          data={appelsPage?.calls ?? []}
          columns={callColumns}
          isLoading={isLoadingAppelsPage}
          pagination
          manualPagination
          pageIndex={appelsPageIndex}
          pageSize={APPELS_PAGE_SIZE}
          pageCount={Math.max(1, Math.ceil((appelsPage?.total ?? 0) / APPELS_PAGE_SIZE))}
          totalItems={appelsPage?.total ?? 0}
          onPageChange={setAppelsPageIndex}
          elementName={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_APPEL'})}
          elementNamePlural={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_APPELS'})}
        />
      </div>

      {isCentrex && (
        <TableMetronic<AcdCall>
          title={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.TITLE_ACD'})}
          data={acdCalls}
          columns={acdColumns}
          pagination
          elementName={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_APPEL'})}
          elementNamePlural={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ELEMENT_APPELS'})}
          subtitle={
            <span className="text-muted fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION.ACD_SUBTITLE'})}
            </span>
          }
        />
      )}
    </div>
  );
};

export default SuiviConsommation;
